/**
 * PainDiaryCard — дневник на болката: сила, характер (бърза A-делта срещу
 * бавна C болка), къде е и накъде се отразява, плюс мозъчна мъгла.
 * Данни: /api/pain-diary/log | /api/pain-diary/analysis
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Activity, AlertTriangle, ArrowRight, ChevronDown, Loader2, Moon,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PainDiaryCard({ isDark = false, cardCls = "" }) {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    intensity: 4, character: "", sites: [], radiation_bg: "",
    night_worse: false, red_flags: [], brain_fog: 0,
  });

  const load = () => axios.get(`${API}/pain-diary/analysis`)
    .then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d) return null;

  const border = isDark ? "border-white/10" : "border-gray-200";
  const chip = (on, danger) => `px-2 py-1 rounded-lg text-[11px] font-semibold border transition-colors ${
    on ? (danger ? "border-rose-500/50 bg-rose-500/15 text-rose-500"
                 : "border-violet-500/50 bg-violet-500/15") : border}`;
  const toggleIn = (key, val) => setForm((f) => ({
    ...f,
    [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val],
  }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/pain-diary/log`, form);
      setD(data.analysis);
      setOpen(false);
      toast.success("Записано");
    } catch { toast.error("Записът не мина"); }
    finally { setBusy(false); }
  };

  return (
    <div className={cardCls || `p-4 rounded-2xl border ${border}`} data-testid="pain-diary-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-bold inline-flex items-center gap-1.5">
            <Activity className="w-4 h-4 text-violet-500" /> Дневник на болката
          </div>
          <p className="text-[11px] opacity-75 mt-0.5" data-testid="pain-summary">
            {d.summary_bg}
          </p>
        </div>
        <button type="button" onClick={() => setOpen((v) => !v)}
          className="px-3 py-1.5 rounded-lg bg-violet-600 text-white text-[12px] font-semibold inline-flex items-center gap-1"
          data-testid="pain-log-toggle">
          Отбележи болка
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>

      {open && (
        <div className={`mt-3 p-3 rounded-xl border ${border}`} data-testid="pain-log-form">
          <label className="text-[11px] font-semibold opacity-80">
            Сила: <span className="tabular-nums" data-testid="pain-intensity-value">{form.intensity}</span>/10
            <input type="range" min="0" max="10" value={form.intensity}
              onChange={(e) => setForm((f) => ({ ...f, intensity: Number(e.target.value) }))}
              className="w-full mt-1 accent-violet-600" data-testid="pain-intensity" />
          </label>

          <div className="mt-2">
            <div className="text-[11px] font-semibold opacity-80 mb-1">Каква е болката</div>
            <div className="flex flex-wrap gap-1.5">
              {d.catalog.characters.map((c) => (
                <button key={c.key} type="button"
                  onClick={() => setForm((f) => ({ ...f, character: c.key }))}
                  className={chip(form.character === c.key)}
                  data-testid={`pain-character-${c.key}`}>{c.label_bg}</button>
              ))}
            </div>
            {form.character && (
              <p className="text-[10px] opacity-75 mt-1" data-testid="pain-character-why">
                {d.catalog.characters.find((c) => c.key === form.character)?.text_bg}
              </p>
            )}
          </div>

          <div className="mt-2">
            <div className="text-[11px] font-semibold opacity-80 mb-1">Къде е</div>
            <div className="flex flex-wrap gap-1.5">
              {d.catalog.sites.map((s) => (
                <button key={s.key} type="button" onClick={() => toggleIn("sites", s.key)}
                  className={chip(form.sites.includes(s.key))}
                  data-testid={`pain-site-${s.key}`}>{s.label_bg}</button>
              ))}
            </div>
          </div>

          <label className="block mt-2 text-[11px] font-semibold opacity-80">
            Накъде се отразява
            <input type="text" value={form.radiation_bg} placeholder="напр. към ухото, към лявата ръка"
              onChange={(e) => setForm((f) => ({ ...f, radiation_bg: e.target.value }))}
              className={`mt-1 w-full px-2 py-1 rounded-lg border bg-transparent text-sm ${border}`}
              data-testid="pain-radiation" />
          </label>

          <label className="text-[11px] font-semibold opacity-80 block mt-2">
            Мозъчна мъгла днес: <span className="tabular-nums">{form.brain_fog}</span>/10
            <input type="range" min="0" max="10" value={form.brain_fog}
              onChange={(e) => setForm((f) => ({ ...f, brain_fog: Number(e.target.value) }))}
              className="w-full mt-1 accent-sky-600" data-testid="pain-fog" />
          </label>

          <div className="mt-2 flex flex-wrap gap-1.5">
            <button type="button"
              onClick={() => setForm((f) => ({ ...f, night_worse: !f.night_worse }))}
              className={chip(form.night_worse)} data-testid="pain-night-worse">
              <Moon className="w-3 h-3 inline mr-1" /> По-силна нощем
            </button>
            {d.catalog.red_flags.map((r) => (
              <button key={r.key} type="button" onClick={() => toggleIn("red_flags", r.key)}
                className={chip(form.red_flags.includes(r.key), true)}
                data-testid={`pain-flag-${r.key}`}>{r.label_bg}</button>
            ))}
          </div>

          <button type="button" onClick={save} disabled={busy}
            className="mt-3 px-3 py-1.5 rounded-lg bg-violet-600 text-white text-[12px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
            data-testid="pain-save">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Запиши
          </button>
        </div>
      )}

      {d.has_data && (
        <div className="mt-3 space-y-2" data-testid="pain-analysis">
          {d.red_flags_bg?.length > 0 && (
            <div className="p-2 rounded-lg bg-rose-500/10 border border-rose-500/30 text-[11px]"
              data-testid="pain-red-flags">
              <div className="font-bold text-rose-500 inline-flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Отбелязали сте предупредителни знаци
              </div>
              <div className="opacity-85 mt-0.5">{d.red_flags_bg.join(" · ")} — това е за лекар, не за дневник.</div>
            </div>
          )}
          {d.fiber_reads?.map((f, i) => (
            <div key={i} className={`p-2 rounded-lg border ${border}`} data-testid={`pain-fiber-${i}`}>
              <div className="text-[11px] font-bold">{f.label_bg}</div>
              <div className="text-[10px] opacity-80">{f.text_bg}</div>
            </div>
          ))}
          {d.referred?.map((r, i) => (
            <div key={i} className={`p-2 rounded-lg border ${
              r.red_flag ? "border-amber-500/40 bg-amber-500/10" : border}`}
              data-testid={`pain-referred-${i}`}>
              <div className="text-[11px] font-bold">Отразена болка · {r.site_bg}</div>
              <div className="text-[10px] opacity-85">{r.text_bg}</div>
            </div>
          ))}
          {d.top_sites_bg?.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="pain-top-sites">
              {d.top_sites_bg.map((s) => (
                <span key={s.label_bg} className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-500/15">
                  {s.label_bg} · {s.count}
                </span>
              ))}
            </div>
          )}
          <div className={`p-2 rounded-lg border ${border}`} data-testid="pain-sleep-link">
            <div className="text-[11px] font-bold">Собствената обезболяваща система</div>
            <div className="text-[10px] opacity-85">{d.sleep_vs_pain_bg}</div>
            <ul className="mt-1 space-y-0.5">
              {d.levers_bg.map((l, i) => (
                <li key={i} className="text-[10px] opacity-75">· {l}</li>
              ))}
            </ul>
          </div>
          {d.fog?.links?.map((l, i) => (
            <div key={i} className={`p-2 rounded-lg border ${border}`} data-testid={`fog-link-${l.twin}`}>
              <div className="text-[11px] font-bold">{l.title_bg}</div>
              <div className="text-[10px] opacity-85">{l.why_bg}</div>
              <div className="text-[10px] font-medium mt-0.5">{l.action_bg}</div>
              <Link to={l.route}
                className="text-[10px] font-bold text-violet-600 dark:text-violet-300 inline-flex items-center gap-1 mt-0.5"
                data-testid={`fog-link-cta-${l.twin}`}>
                Отвори {l.twin_bg} <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          ))}
        </div>
      )}

      <p className="text-[9px] opacity-50 mt-2">{d.note_bg || d.education_bg}</p>
    </div>
  );
}
